
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface AuthCheckProps {
  children: ReactNode;
}

export default function AuthCheck({ children }: AuthCheckProps) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Main portal URL - update this to match your production URL
  const AUTH_PORTAL_URL = "https://auth.venturecatalysts.co.uk";

  useEffect(() => {
    // Check if the user is authenticated
    const checkAuth = async () => {
      try {
        setIsLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Error checking authentication:", error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setIsAuthenticated(!!session);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  if (loading || isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-brand-primary mx-auto" />
          <p className="mt-4 text-brand-dark">Checking authentication...</p>
        </div>
      </div>
    );
  }
  
  if (!user && !isAuthenticated) {
    // Redirect to the auth portal with return URL
    const currentUrl = encodeURIComponent(window.location.href);
    const loginUrl = `${AUTH_PORTAL_URL}/login?returnUrl=${currentUrl}`;
    
    window.location.href = loginUrl;
    
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center p-4">
        <div className="max-w-md text-center">
          <h2 className="mb-4 text-2xl font-bold">Authentication Required</h2>
          <p className="mb-6">
            You need to be logged in to access Lean Venture Catalysts tools. Redirecting to Auth Portal...
          </p>
          <Loader2 className="h-10 w-10 animate-spin text-brand-primary mx-auto" />
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
